import { useCallback, useEffect, useState } from "react";

import _ from "lodash";
import { useRouter } from "next/router";
// @ts-expect-error TS2307
import { QueryArgs } from "types";

import {
  searchParamsToDictionary as mercerSearchParamsToDictionary,
  dictionaryToSearchParams as mercerDictionaryToSearchParams,
  defaultQueryString as mercerDefaultQueryString,
  comptryxDefaultQueryString,
  cartaDefaultQueryString,
} from "utils/mercer";
import { dictionaryToSearchParams, searchParamsToDictionary, makeQueryString } from "utils/utils";

const defaultQueryString = dictionaryToSearchParams({
  jobFamily: ["Software Engineering"],
  level: ["IC3"],
});

export function getSafeExplorerSearchQueryString(rawQueryString?: string): string {
  const queryString = rawQueryString || defaultQueryString;
  return queryString;
}

export function mercerGetSafeExplorerSearchQueryString(
  rawQueryString?: string,
  activeDataset?: string,
): string {
  const queryString =
    rawQueryString ||
    (activeDataset === "mercer" ? mercerDefaultQueryString : comptryxDefaultQueryString);

  return queryString;
}

export function benchmarkingGetSafeExplorerSearchQueryString(rawQueryString?: string): string {
  const queryString = rawQueryString || cartaDefaultQueryString;

  return queryString;
}

export function useSearchRouteInfo() {
  const router = useRouter();

  const rawQueryString = router.query.query?.[0];
  const queryString = getSafeExplorerSearchQueryString(rawQueryString);

  let baseRoute = _.initial(router.pathname.split("/")).join("/");

  let focusedJobTitle = "";

  // if we remove the old explore pages we can remove the default case and just use this one
  // if (router.pathname.includes("/s/")) {
  //   baseRoute = baseRoute.replace("[title]", router.query.title as string);
  //   focusedJobTitle = router.query.title as string;
  // }

  const [query, setQuery] = useState(() => {
    const searchParams = new URLSearchParams(queryString);
    return searchParamsToDictionary(searchParams);
  });

  useEffect(() => {
    setQuery(() => {
      const searchParams = new URLSearchParams(queryString);
      return searchParamsToDictionary(searchParams);
    });
  }, [queryString]);

  const replaceQuery = useCallback(
    (q: Partial<QueryArgs>, opts?: { preservePage?: boolean; scroll?: boolean }) => {
      router.push(
        `${baseRoute}/${dictionaryToSearchParams({
          ...query,
          ...q,
          offset: opts?.preservePage ? query.offset : 0,
        })}`,
        undefined,
        { scroll: opts?.scroll ?? false },
      );
    },
    [baseRoute, query, router],
  );

  let searchMode: "title" | "family & level" = "title";
  if (!_.isEmpty(query.jobFamily) && !_.isEmpty(query.level)) {
    searchMode = "family & level";
  } else if (!_.isEmpty(query.title)) {
    searchMode = "title";
  } else {
    throw new Error("No Reasonable Search Mode");
  }

  return {
    focusedJobTitle,
    queryString,
    baseRoute,
    query,
    replaceQuery,
    searchMode,
  };
}

export function useMercerSearchRouteInfo() {
  const router = useRouter();
  const activeDataset = router.query.dataset as string;

  const rawQueryString = router.query.query?.[0];
  const queryString = mercerGetSafeExplorerSearchQueryString(rawQueryString, activeDataset);

  let baseRoute = `/benchmarking/${activeDataset}`;

  const [query, setQuery] = useState(() => {
    const searchParams = new URLSearchParams(queryString);
    return mercerSearchParamsToDictionary(searchParams);
  });

  useEffect(() => {
    setQuery(() => {
      const searchParams = new URLSearchParams(queryString);
      const dict = mercerSearchParamsToDictionary(searchParams);
      return dict;
    });
  }, [queryString]);

  const replaceQuery = useCallback(
    (q: Partial<QueryArgs>, opts?: { preservePage?: boolean; scroll?: boolean }) => {
      router.push(
        `${baseRoute}/${mercerDictionaryToSearchParams({
          ...query,
          ...q,
        })}`,
        undefined,
        { scroll: opts?.scroll ?? false },
      );
    },
    [baseRoute, query, router],
  );

  return {
    queryString,
    baseRoute,
    query,
    replaceQuery,
  };
}

export function useBenchmarkingSearchRouteInfo() {
  const router = useRouter();
  const activeDataset = router.query.dataset as string;

  let baseRoute = `/benchmarking/${activeDataset}`;

  const replaceQuery = useCallback(
    (
      currentQuery: Partial<QueryArgs>,
      q: Partial<QueryArgs>,
      opts?: { preservePage?: boolean; scroll?: boolean },
    ) => {
      router.push(`${baseRoute}${makeQueryString({ ...currentQuery, ...q })}`, undefined, {
        scroll: opts?.scroll ?? false,
      });
    },
    [baseRoute, router],
  );

  return {
    replaceQuery,
  };
}

export function useIsLoading(): boolean {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleStart = () => setIsLoading(true);
    const handleComplete = () => setIsLoading(false);

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  }, []);

  return isLoading;
}

export function useIsLoadingSearchData(): boolean {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let previousPath = router.asPath.split("?")[0];

    const handleRouteChange = (url: string) => {
      const [currentPath] = url.split("?");
      // remove /benchmarking from currentPath
      const fixedCurrentPath = currentPath.replace("/benchmarking", "");
      // console.log({ fixedCurrentPath, currentPath, previousPath });
      if (fixedCurrentPath !== previousPath) {
        setIsLoading(true);
      }
      previousPath = fixedCurrentPath;
    };

    const handleRouteChangeComplete = () => {
      setIsLoading(false);
    };

    router.events.on("routeChangeStart", handleRouteChange);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeError", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeError", handleRouteChangeComplete);
    };
  }, [router]);

  return isLoading;
}
